import React, { useState } from "react";
import { useNavigate, useLocation, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ArrowRight, Eye, EyeOff, ArrowLeft, Mail, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth, formatApiError, API } from "../contexts/AuthContext";
import "../lms.css";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // OTP-challenge state
  const [otp, setOtp] = useState(null); // { challenge_id, email_hint, device_label, city }
  const [otpCode, setOtpCode] = useState("");
  const { login, refreshMe } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const from = location.state?.from || null;
  const kicked = params.get("kicked") === "1";
  const reason = params.get("reason"); // "account_disabled" | "forced_logout"

  const redirectAfter = (user) => {
    const dest = from || (user.role === "admin" ? "/admin" : user.role === "mentor" ? "/mentor" : "/student");
    navigate(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return; // hard block double-submit (fix for rapid clicks firing multiple OTP sends)
    setError("");
    setLoading(true);
    try {
      // Call login directly so we can detect the otp_required branch.
      // The context's login() sets the user on success — we only use it for
      // the trusted-device happy path.
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data?.otp_required) {
        setOtp({
          challenge_id: data.challenge_id,
          email_hint: data.email_hint,
          device_label: data.device_label,
          city: data.city,
        });
        return;
      }
      // Trusted device → full login succeeded on the server.
      // Sync AuthContext user so guards work.
      await refreshMe();
      redirectAfter(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Friendlier copy if we hit the email-delivery path (usually Resend
      // burst limit from rapid login clicks — backend already auto-retried once).
      if (err.response?.status === 503 && typeof detail === "string" && detail.toLowerCase().includes("verification email")) {
        setError("We couldn't send the sign-in code just now — wait a few seconds and try again. If the issue persists, double-check your email address.");
      } else {
        setError(formatApiError(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-otp`, {
        challenge_id: otp.challenge_id,
        code: otpCode.trim(),
      });
      await refreshMe();
      redirectAfter(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Suppressed eslint warning: `login` from context is intentionally unused
  // here because we use a direct axios call to catch the otp_required branch.
  void login;

  return (
    <div className="lms-root min-h-screen flex flex-col">
      <div aria-hidden className="fixed inset-0 lms-grain" />

      {/* Top bar — back to landing */}
      <div className="relative z-10 px-6 md:px-10 pt-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
          data-testid="login-back-home"
        >
          <ArrowLeft size={16} /> Back to site
        </Link>
        <span className="lms-mono">LogicsWallah · Student Portal</span>
      </div>

      {/* Center */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-md lms-glass-strong p-8 md:p-10"
          data-testid="login-card"
        >
          <div className="lms-mono text-[#5ceeff] mb-3">— Welcome back, Logician</div>
          <h1 className="lms-display text-white text-4xl md:text-5xl leading-[1.05] mb-2">
            Sign in to your
            <br />
            <em className="text-[#5ceeff] not-italic" style={{ fontStyle: "italic" }}>study universe.</em>
          </h1>
          <p className="text-white/55 text-sm mt-3 mb-8 leading-relaxed">
            Your progress, lectures, MCQs and mentor — all waiting for you.
          </p>

          {kicked && !otp && (
            <div className="mb-5 p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm flex items-start gap-2"
                 data-testid="login-kicked-banner">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong className="text-white">You were signed out remotely</strong> because your account signed in on a new device.
                If this wasn't you, reset your password after signing in.
              </div>
            </div>
          )}

          {reason === "forced_logout" && !otp && (
            <div className="mb-5 p-3 rounded-[14px] border border-amber-400/30 bg-amber-500/10 text-amber-200 text-sm flex items-start gap-2"
                 data-testid="login-forced-logout-banner">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong className="text-white">You were signed out from every device by an administrator.</strong>{" "}
                Please re-enter your password to sign back in. If you didn't expect this, contact your super-admin.
              </div>
            </div>
          )}

          {reason === "account_disabled" && !otp && (
            <div className="mb-5 p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm flex items-start gap-2"
                 data-testid="login-account-disabled-banner">
              <ShieldCheck size={14} className="mt-0.5 shrink-0" />
              <div>
                <strong className="text-white">This account has been disabled.</strong>{" "}
                You can't sign in until a super-admin re-activates it. Please reach out to your super-admin for help.
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {!otp ? (
              <motion.div
                key="pwd"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
              >
                <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                  <div>
                    <label className="lms-label">Email</label>
                    <input
                      type="email"
                      className="lms-input"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <label className="lms-label">Password</label>
                    <div className="relative">
                      <input
                        type={show ? "text" : "password"}
                        className="lms-input pr-12"
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShow(!show)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition"
                        aria-label={show ? "Hide password" : "Show password"}
                        data-testid="login-password-toggle"
                      >
                        {show ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Link
                        to="/forgot-password"
                        className="text-xs text-white/50 hover:text-[#5ceeff] transition"
                        data-testid="login-forgot"
                      >
                        Forgot password?
                      </Link>
                    </div>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm"
                      data-testid="login-error"
                    >
                      {error}
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="lms-btn lms-btn-primary w-full justify-center h-12 text-[15px] disabled:opacity-50"
                    data-testid="login-submit"
                  >
                    {loading ? "Signing in…" : "Sign in"}
                    {!loading && <ArrowRight size={16} />}
                  </button>
                </form>
              </motion.div>
            ) : (
              /* OTP challenge view */
              <motion.div
                key="otp"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
                data-testid="login-otp-view"
              >
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                       style={{ background: "rgba(92,238,255,0.12)", border: "1px solid rgba(92,238,255,0.35)" }}>
                    <Mail size={15} className="text-[#5ceeff]" />
                  </div>
                  <div className="lms-mono text-[10px] uppercase tracking-[0.18em] text-[#5ceeff]">
                    — New-device verification
                  </div>
                </div>
                <h2 className="lms-display text-white text-2xl leading-tight mb-2">
                  Check your email for the code.
                </h2>
                <p className="text-white/55 text-[13px] leading-relaxed mb-5">
                  We sent a 6-digit sign-in code to <strong className="text-white/85">{otp.email_hint}</strong>.
                  Enter it below to finish signing in on <em style={{ fontStyle: "italic" }}>{otp.device_label}</em>
                  {otp.city && otp.city !== "—" ? ` · ${otp.city}` : ""}.
                </p>

                <form onSubmit={submitOtp} className="space-y-5" data-testid="login-otp-form">
                  <div>
                    <label className="lms-label">6-digit code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      className="lms-input text-center tracking-[0.55em] text-xl font-mono"
                      placeholder="••••••"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      autoFocus
                      data-testid="login-otp-code"
                    />
                    <div className="text-[11px] text-white/40 mt-1.5">Code expires in 10 minutes.</div>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="p-3 rounded-[14px] border border-rose-400/30 bg-rose-500/10 text-rose-200 text-sm"
                      data-testid="login-otp-error"
                    >
                      {error}
                    </motion.div>
                  )}

                  <button
                    type="submit"
                    disabled={loading || otpCode.length !== 6}
                    className="lms-btn lms-btn-primary w-full justify-center h-12 text-[15px] disabled:opacity-50"
                    data-testid="login-otp-submit"
                  >
                    {loading ? "Verifying…" : "Verify & sign in"}
                    {!loading && <KeyRound size={15} />}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setOtp(null); setOtpCode(""); setError(""); }}
                    className="w-full text-xs text-white/45 hover:text-[#5ceeff] transition text-center"
                    data-testid="login-otp-back"
                  >
                    ← Use a different email
                  </button>
                </form>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="lms-divider my-8" />

          <div className="space-y-3 text-center">
            <p className="text-sm text-white/60">
              New to LogicsWallah?
            </p>
            <Link
              to="/apply"
              className="lms-btn lms-btn-ghost w-full justify-center h-11 text-sm"
              data-testid="login-apply-btn"
            >
              Apply for a batch <ArrowRight size={14} />
            </Link>
            <Link
              to="/"
              className="block text-xs text-white/45 hover:text-[#5ceeff] transition"
              data-testid="login-explore"
            >
              or explore all batches →
            </Link>
          </div>
        </motion.div>
      </div>

      {/* Decorative glow */}
      <div aria-hidden className="fixed bottom-[-30%] left-1/2 -translate-x-1/2 w-[1100px] h-[700px] rounded-full pointer-events-none"
           style={{ background: "radial-gradient(circle, rgba(0,229,255,0.14), transparent 60%)", filter: "blur(60px)" }} />
    </div>
  );
}
